<template>
  <div
    class="menu-node"
    :class="{
      'menu-node-top': depth === 1,
      'menu-item-dropdown': item.type === 'dropdown',
      'menu-item-value': item.type === 'value',
      'menu-item-link': item.type === 'link',
      'menu-item-code': item.type === 'code'
    }"
  >
    <div class="menu-item-row">
      <span class="menu-drag-handle" @click.stop>
        <font-awesome-icon icon="grip-vertical" />
      </span>
      <font-awesome-icon
        v-if="item.type === 'dropdown'"
        icon="caret-down"
        class="dropdown-icon"
      />
      <font-awesome-icon
        v-else-if="item.type === 'value'"
        icon="hashtag"
        class="value-icon"
      />
      <font-awesome-icon
        v-else-if="item.type === 'code'"
        icon="code"
        class="code-icon"
      />
      <font-awesome-icon
        v-else
        icon="link"
        class="link-icon"
      />

      <input
        v-if="menuEditor.editingItemId === item.id"
        v-model="item.label"
        class="label-input"
        @blur="menuEditor.editingItemId = null"
        @keyup.enter="menuEditor.editingItemId = null"
        @vue:mounted="menuEditor.focusInput"
      />
      <span
        v-else
        class="item-label"
        @click="menuEditor.startEditing(item.id)"
      >{{ item.label }}</span>

      <span class="item-page-ref value-result" v-if="item.type === 'value'">
        {{ menuEditor.previewText(item) }}
      </span>
      <span class="item-page-ref" v-else-if="item.pageUniqueTitle">
        {{ item.pageUniqueTitle }}
      </span>

      <span class="item-actions">
        <span
          v-if="menuEditor.supportsRecordLinks && item.type === 'link'"
          class="login-toggle-btn"
          :class="{ active: item.inheritAccessKey }"
          @click="menuEditor.toggleFlag(item, 'inheritAccessKey')"
          :title="item.inheritAccessKey ? 'This link keeps the record open on this page (click to open the page on its own)' : 'Click to keep the record open on this page when the link is opened'"
        >
          <font-awesome-icon icon="key" />
        </span>
        <span
          v-if="menuEditor.supportsButtonLinks && item.type === 'link'"
          class="display-button-btn"
          :class="{ active: item.displayAsButton }"
          @click="menuEditor.toggleFlag(item, 'displayAsButton')"
          :title="item.displayAsButton ? 'Shown as a button (click to show it as a link)' : 'Click to show this link as a button'"
        >
          <font-awesome-icon icon="square" />
        </span>
        <span
          v-if="depth === 1"
          class="align-right-btn"
          :class="{ active: item.alignRight }"
          @click="menuEditor.toggleAlignRight(item)"
          :title="item.alignRight ? 'Aligned to the right side of the menu (click to move it back to the left)' : 'Click to align this item to the right side of the menu'"
        >
          <font-awesome-icon icon="align-right" />
        </span>
        <span
          v-if="menuEditor.appHasLogin"
          class="login-toggle-btn"
          :class="{ active: item.requiresLogin }"
          @click="menuEditor.toggleFlag(item, 'requiresLogin')"
          :title="item.requiresLogin ? 'Visible only when logged in (click to make always visible)' : 'Click to show only when logged in'"
        >
          <font-awesome-icon :icon="item.requiresLogin ? 'lock' : 'lock-open'" />
        </span>
        <span
          v-if="menuEditor.appHasLogin"
          class="hide-logged-in-btn"
          :class="{ active: item.hideWhenLoggedIn }"
          @click="menuEditor.toggleFlag(item, 'hideWhenLoggedIn')"
          :title="item.hideWhenLoggedIn ? 'Hidden when logged in (click to always show)' : 'Click to hide when logged in'"
        >
          <font-awesome-icon :icon="item.hideWhenLoggedIn ? 'eye-slash' : 'eye'" />
        </span>
        <span
          v-if="menuEditor.appHasLogin && item.type === 'link'"
          class="login-toggle-btn"
          :class="{ active: linkHasRules }"
          @click="menuEditor.openLinkPermissionRules(item)"
          :title="linkPermissionTitle"
          :aria-label="linkPermissionTitle"
        >
          <font-awesome-icon icon="user-shield" />
        </span>
        <span class="remove-btn" @click="$emit('remove')" title="Remove from menu">x</span>
      </span>
    </div>

    <div v-if="item.type === 'code' && menuEditor.supportsCodeButtons" class="menu-code-editor">
      <div class="menu-code-label">JavaScript</div>
      <p class="menu-code-hint">Runs when this button is clicked. It can call a function from the custom code files in app settings.</p>
      <div class="menu-code-box">
        <JsCodeEditor v-model="item.code" :aria-label="`JavaScript for ${item.label || 'button'}`" />
      </div>
    </div>

    <MenuValueLinkDrop v-if="item.type === 'value'" :item="item" />
    <MenuValueConfig
      v-if="item.type === 'value'"
      :item="item"
      :config="item.valueConfig"
      :spreadsheets="menuEditor.spreadsheets"
      :application="menuEditor.application"
    />

    <div v-if="canHoldChildren" class="dropdown-children">
      <draggable
        v-model="item.children"
        :group="{ name: 'menu', pull: true, put: true }"
        item-key="id"
        handle=".menu-drag-handle"
        filter=".dropdown-children"
        :preventOnFilter="false"
        ghost-class="drag-ghost"
        :move="menuEditor.checkMenuMove"
        :data-menu-depth="depth + 1"
        class="dropdown-children-draggable"
      >
        <template #item="{ element: child, index: childIdx }">
          <MenuItemNode
            :item="child"
            :depth="depth + 1"
            @remove="removeChild(childIdx)"
          />
        </template>
        <template #footer>
          <div
            v-if="!item.children || item.children.length === 0"
            class="empty-dropdown-hint"
          >
            {{ menuEditor.supportsCodeButtons ? 'Drag pages, values, or menus here, or add a code button' : 'Drag pages, values, or menus here' }}
          </div>
        </template>
      </draggable>
      <div class="menu-node-actions">
        <button type="button" class="add-child-value-btn" @click="addChildValue">
          <font-awesome-icon icon="hashtag" /> add value
        </button>
        <button type="button" class="add-child-value-btn" @click="addChildMenu">
          <font-awesome-icon icon="caret-down" /> add menu
        </button>
        <button v-if="menuEditor.supportsCodeButtons" type="button" class="add-child-value-btn" @click="addChildCode">
          <font-awesome-icon icon="code" /> add code button
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import JsCodeEditor from '../JsCodeEditor.vue'
import MenuValueConfig from './MenuValueConfig.vue'
import MenuValueLinkDrop from './MenuValueLinkDrop.vue'
import canPlaceMenuItem from './menu-nesting.js'
import { hasUserPermissionRules } from '../../../../shared/user-permission-rules.js'
import { userPermissionTip } from '../../lib/view-icon-tips.js'

const MAX_MENU_DEPTH = canPlaceMenuItem.MAX_MENU_DEPTH

export default {
  name: 'MenuItemNode',
  components: { draggable, JsCodeEditor, MenuValueConfig, MenuValueLinkDrop },
  inject: ['menuEditor'],
  props: {
    item: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      required: true
    }
  },
  emits: ['remove'],
  computed: {
    canHoldChildren () {

      return (this.item.type === 'dropdown' || this.item.type === 'value') && this.depth < MAX_MENU_DEPTH
    },
    linkHasRules () {

      return hasUserPermissionRules(this.item)
    },
    linkPermissionTitle () {

      return userPermissionTip(this.linkHasRules, 'this link')
    }
  },
  created () {

    if (this.canHoldChildren && !Array.isArray(this.item.children)) this.item.children = []
  },
  methods: {
    removeChild (index) {

      this.item.children.splice(index, 1)
    },
    addChildValue () {

      if (!Array.isArray(this.item.children)) this.item.children = []

      this.item.children.push(this.menuEditor.newValueItem())
    },
    addChildMenu () {

      if (!Array.isArray(this.item.children)) this.item.children = []

      this.item.children.push(this.menuEditor.newDropdownItem())
    },
    addChildCode () {

      if (!Array.isArray(this.item.children)) this.item.children = []

      this.item.children.push(this.menuEditor.newCodeItem())
    }
  }
}
</script>

<style scoped>
.menu-node-top {
  background-color: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  margin-bottom: 6px;
}

.menu-node:not(.menu-node-top) {
  display: flex;
  flex-direction: column;
  background-color: #161614;
  border: 1px solid #252522;
  border-radius: 3px;
  margin-bottom: 4px;
}

.menu-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
}

.menu-node:not(.menu-node-top) .menu-item-row {
  padding: 6px 10px;
}

.menu-drag-handle {
  cursor: grab;
  color: #7a7a76;
  font-size: 0.65rem;
  padding: 2px;
}

.dropdown-icon {
  color: #f0ad4e;
  font-size: 0.8rem;
}

.value-icon {
  color: #9ad12b;
  font-size: 0.75rem;
}

.link-icon {
  color: #5bc0de;
  font-size: 0.7rem;
}

.item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #e8e8e6;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
}

.item-label:hover {
  background-color: #2a2a27;
}

.label-input {
  flex: 1;
  background-color: #111;
  border: 1px solid #9ad12b;
  color: #e8e8e6;
  font-size: 0.875rem;
  padding: 2px 4px;
  border-radius: 3px;
  outline: none;
}

.item-page-ref {
  font-size: 0.7rem;
  color: #a8a8a4;
  font-family: monospace;
  white-space: nowrap;
}

.value-result {
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.1);
  border-radius: 10px;
  padding: 0 8px;
}

.item-actions {
  display: flex;
  gap: 4px;
}

.align-right-btn,
.login-toggle-btn,
.hide-logged-in-btn,
.display-button-btn,
.remove-btn {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  font-size: 11px;
}

.align-right-btn,
.login-toggle-btn,
.hide-logged-in-btn,
.display-button-btn {
  color: #a8a8a4;
  border: 1px #3a3a37 solid;
}

.align-right-btn.active {
  color: #5bc0de;
  border-color: #5bc0de;
  background-color: rgba(91, 192, 222, 0.1);
}

.login-toggle-btn.active {
  color: #9ad12b;
  border-color: #9ad12b;
  background-color: rgba(154, 209, 43, 0.1);
}

.display-button-btn.active {
  color: #5bc0de;
  border-color: #5bc0de;
  background-color: rgba(91, 192, 222, 0.1);
}

.hide-logged-in-btn.active {
  color: #f0ad4e;
  border-color: #f0ad4e;
  background-color: rgba(240, 173, 78, 0.1);
}

.remove-btn {
  font-size: 12px;
  font-weight: bolder;
  color: #f82471;
  border: 1px #f82471 solid;
}

.menu-code-editor {
  padding: 0 12px 10px 36px;
}

.menu-code-label {
  font-size: 0.75rem;
  color: #c8c8c4;
  margin-bottom: 2px;
}

.menu-code-hint {
  margin: 0 0 6px;
  font-size: 0.72rem;
  color: #8a8a86;
  line-height: 1.35;
}

.menu-code-box {
  height: 9rem;
}

.menu-code-box :deep(.js-code) {
  height: 100%;
}

.code-icon {
  color: #c6ee7a;
}

.dropdown-children {
  padding: 4px 10px 8px 18px;
  border-top: 1px solid #2e2e2b;
}

.dropdown-children-draggable {
  min-height: 36px;
}

.empty-dropdown-hint {
  font-size: 0.75rem;
  color: #a8a8a4;
  padding: 8px 10px;
  text-align: center;
  border: 1px dashed #2e2e2b;
  border-radius: 3px;
}

.menu-node-actions {
  display: flex;
  gap: 12px;
  margin-top: 6px;
}

.add-child-value-btn {
  background: none;
  border: 0;
  color: #9ad12b;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
}

.add-child-value-btn:hover {
  color: #b8f046;
}
</style>
